import { type ReactNode } from 'react';
import { Building2, Info } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@design-system';
import { cn } from '@client/lib';
import { AM_BEST_RATING_INFO } from '@client/util/amBestRatingData';

import { type GuidedPlanningQuote } from '@/features/guided-financial-planning/hooks';
import { ProductDetailsHoverCard } from '@/features/guided-financial-planning/components/quote-card';

export interface CarrierInfo {
  carrierName: string;
  productName: string;
  logoSrc: string;
  creditRating?: string;
  quote?: GuidedPlanningQuote;
  isCcrcTypeA?: boolean;
}

/**
 * Logo box — opens the product-details hover card when a quote is present,
 * unless `infoIcon` is set (the dedicated info icon is the trigger instead).
 */
function CarrierBadgeLogo({
  carrierInfo,
  infoIcon = false,
}: {
  carrierInfo: CarrierInfo;
  infoIcon?: boolean;
}) {
  const { carrierName, logoSrc, creditRating } = carrierInfo;

  if (carrierInfo.quote && !infoIcon) {
    return (
      <ProductDetailsHoverCard quote={carrierInfo.quote}>
        <button
          type="button"
          className={cn(
            'bg-background focus-visible:ring-primary size-10 shrink-0 cursor-help rounded-sm border p-1.5 focus-visible:outline-none focus-visible:ring-2',
            { 'size-14': creditRating },
          )}
        >
          <img
            src={logoSrc}
            alt={carrierName}
            className="size-full object-contain"
          />
        </button>
      </ProductDetailsHoverCard>
    );
  }

  return (
    <div
      className={cn(
        'bg-background flex size-10 shrink-0 items-center justify-center rounded-sm border p-1.5',
        { 'size-14': creditRating },
      )}
    >
      {carrierInfo.isCcrcTypeA ? (
        <Building2 className="text-primary size-full" />
      ) : (
        <img
          src={logoSrc}
          alt={carrierName}
          className="size-full object-contain"
        />
      )}
    </div>
  );
}

/**
 * Credit rating + product name + carrier name. `nameAction` renders inline next
 * to the product name (e.g. a rename pencil); `columnFooter` renders beneath the
 * carrier name.
 */
function CarrierBadgeInfo({
  carrierInfo,
  nameAction,
  columnFooter,
  infoIcon = false,
  onNameClick,
}: {
  carrierInfo: CarrierInfo;
  nameAction?: ReactNode;
  columnFooter?: ReactNode;
  /** Render a dedicated info icon as the product-details hover trigger instead of the product name. */
  infoIcon?: boolean;
  /** When set, the product name becomes a button invoking this handler (e.g. open a slide-out). */
  onNameClick?: () => void;
}) {
  const { carrierName, productName, creditRating } = carrierInfo;
  const ratingInfo = creditRating ? AM_BEST_RATING_INFO[creditRating] : null;

  return (
    <div className="min-w-0">
      {creditRating && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="cursor-help text-xs font-medium text-sky-600">
                Credit Rating {creditRating}
              </p>
            </TooltipTrigger>
            {ratingInfo && (
              <TooltipContent className="max-w-sm">
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold">{ratingInfo.title}</p>
                  <p className="text-sm">{ratingInfo.description}</p>
                </div>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )}
      <div className="flex items-center gap-1.5">
        {carrierInfo.quote && infoIcon ? (
          <>
            {onNameClick ? (
              <button
                type="button"
                onClick={onNameClick}
                className="focus-visible:ring-primary min-w-0 truncate text-left text-sm font-semibold text-gray-950 hover:underline focus-visible:outline-none focus-visible:ring-2"
              >
                {productName}
              </button>
            ) : (
              <p className="min-w-0 truncate text-sm font-semibold text-gray-950">{productName}</p>
            )}
            <ProductDetailsHoverCard quote={carrierInfo.quote}>
              <button
                type="button"
                aria-label="Product details"
                className="text-muted-foreground hover:text-gray-950 focus-visible:ring-primary shrink-0 cursor-help focus-visible:outline-none focus-visible:ring-2"
              >
                <Info className="size-3.5" />
              </button>
            </ProductDetailsHoverCard>
          </>
        ) : carrierInfo.quote ? (
          <ProductDetailsHoverCard quote={carrierInfo.quote}>
            <button
              type="button"
              className="focus-visible:ring-primary min-w-0 cursor-help truncate text-left text-sm font-semibold text-gray-950 focus-visible:outline-none focus-visible:ring-2"
            >
              {productName}
            </button>
          </ProductDetailsHoverCard>
        ) : (
          <p className="min-w-0 truncate text-sm font-semibold text-gray-950">{productName}</p>
        )}
        {nameAction && <span className="text-muted-foreground shrink-0">{nameAction}</span>}
      </div>
      <p className="text-muted-foreground text-xs">{carrierName}</p>
      {columnFooter && <div className="mt-1.5">{columnFooter}</div>}
    </div>
  );
}

/**
 * Boxed carrier badge: gray rounded container with the optional footer rendered
 * inline beneath the carrier name (indented under the text column).
 */
export function CarrierBadge({
  carrierInfo,
  nameAction,
  footer,
}: {
  carrierInfo: CarrierInfo;
  /** Optional content rendered inline next to the product name (e.g. a rename pencil). */
  nameAction?: ReactNode;
  /** Optional content rendered beneath the carrier name (e.g. an editable policy-name chip). */
  footer?: ReactNode;
}) {
  return (
    <div className="bg-muted flex items-center gap-2 rounded-lg border p-2">
      <CarrierBadgeLogo carrierInfo={carrierInfo} />
      <CarrierBadgeInfo
        carrierInfo={carrierInfo}
        nameAction={nameAction}
        columnFooter={footer}
      />
    </div>
  );
}

/**
 * Backgroundless carrier badge: no container chrome, with the optional footer
 * rendered full-width beneath the logo + info row (spans the full width).
 */
export function CarrierBadgePlain({
  carrierInfo,
  onNameClick,
  footer,
}: {
  carrierInfo: CarrierInfo;
  /** When set, clicking the product name invokes this handler (e.g. open a slide-out). */
  onNameClick?: () => void;
  /** Optional content rendered full-width beneath the logo + info row. */
  footer?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <CarrierBadgeLogo
          carrierInfo={carrierInfo}
          infoIcon
        />
        <CarrierBadgeInfo
          carrierInfo={carrierInfo}
          infoIcon
          onNameClick={onNameClick}
        />
      </div>
      {footer && <div>{footer}</div>}
    </div>
  );
}
